import { useAuth0 } from "@auth0/auth0-react";
import styles from "./groups.module.css";
import Loader from "../../../components/Loader";
import Footer from "../../../components/Footer";
import { ArrowLeft, Home, X } from "react-feather";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Slide, Snackbar, Typography } from "@mui/material";
import { TableLoaderSmall } from "../../../components/LoaderC";
import { Helmet } from "react-helmet";
import { useCallback, useEffect, useRef, useState } from "react";
import AddGroup from "./components/AddGroup";
import axios from 'axios';
import { toast } from 'react-smart-toaster';
import GroupFilter from "./components/GroupFilter";
import GroupCard from "./components/GroupCard";
import withCustomAuthenticationRequired from "../../../auth/withCustomAuthenticationRequired";
import useDebouncedValue from "../../../hooks/useDebouncedValue";

const CHUNK_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const Groups = () => {
    const { getAccessTokenSilently } = useAuth0();
    const [loading, setLoading] = useState(false);
    const [contentLoading, setContentLoading] = useState(true);
    const [refetching, setRefetching] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [loadMoreError, setLoadMoreError] = useState(false);
    const [allData, setAllData] = useState([]);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState("");
    const [filterData, setFilterData] = useState({ searchKey: "", type: "" });
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectecId, setSelectecId] = useState(0);
    const [editUserData, setEditUserData] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);

    const debouncedSearch = useDebouncedValue(filterData.searchKey, SEARCH_DEBOUNCE_MS);
    const sentinelRef = useRef(null);
    const requestSeq = useRef(0);
    const hasLoadedOnceRef = useRef(false);

    const fetchGroups = useCallback(async (currentOffset, replace) => {
        const seq = ++requestSeq.current;

        let activeIndicator = "loadingMore";
        if (replace && !hasLoadedOnceRef.current) {
            activeIndicator = "contentLoading";
            setContentLoading(true);
        } else if (replace) {
            activeIndicator = "refetching";
            setRefetching(true);
        } else {
            setLoadingMore(true);
        }
        setLoadMoreError(false);

        let params = { offset: currentOffset, limit: CHUNK_SIZE };
        if (debouncedSearch.trim() !== "") params.search = debouncedSearch.trim();
        if (filterData.type !== "" && filterData.type !== "all") params.type = filterData.type;

        try {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            const response = await axios.get(process.env.REACT_APP_USER_API_URL + "v1/campaign/groups/paginated", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: params,
            });

            // Ignore responses from requests superseded by newer filters
            if (seq !== requestSeq.current) return;

            const newChunk = response.data?.data || [];
            const meta = response.data?.meta || {};
            const nextOffset = meta?.next_offset ?? (currentOffset + newChunk.length);

            setAllData((prev) => (replace ? newChunk : [...prev, ...newChunk]));
            setOffset(nextOffset);
            setHasMore(meta?.has_more ?? (newChunk.length > 0 && nextOffset < (meta?.total ?? 0)));
            setLoadError(false);
            hasLoadedOnceRef.current = true;
        } catch (error) {
            if (seq !== requestSeq.current) return;
            console.error('[Groups] Failed to load group list', error);
            toast.error(error?.response?.data?.message || 'Unable to load groups. Please try again.');

            if (replace) {
                setLoadError(true);
                setAllData([]);
                setHasMore(false);
            } else {
                setLoadMoreError(true);
            }
        } finally {
            if (seq === requestSeq.current) {
                if (activeIndicator === "contentLoading") setContentLoading(false);
                else if (activeIndicator === "refetching") setRefetching(false);
                else setLoadingMore(false);
            }
        }
    }, [getAccessTokenSilently, debouncedSearch, filterData.type]);

    // Load the first chunk whenever search or filters change (or after a group is added)
    useEffect(() => {
        fetchGroups(0, true);
    }, [fetchGroups, reloadKey]);

    // Infinite scroll: load the next chunk when the sentinel enters the viewport
    useEffect(() => {
        const node = sentinelRef.current;
        if (!node) return;

        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting) && hasMore && !loadingMore && !contentLoading && !refetching && !loadMoreError) {
                fetchGroups(offset, false);
            }
        }, { threshold: 0.1 });

        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, loadingMore, contentLoading, refetching, loadMoreError, offset, fetchGroups]);

    // A new group may not match the active search/filters, so reload the first chunk instead of prepending it
    const reloadList = () => {
        setReloadKey((prev) => prev + 1);
    }

    const confirmDelete = async () => {
        setDeleteModalOpen(false);
        setLoading(true);
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        axios.delete(process.env.REACT_APP_USER_API_URL + 'v1/campaign/groups/' + selectecId, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        }).then(response => {
            setSnackbarOpen(true);
            setSnackbarMsg(response.data.message);
            setLoading(false);

            setAllData(prev => {
                return prev?.filter(i => i?.id !== selectecId);
            });
            setSelectecId(0);
        }).catch(error => {
            if (typeof error.response !== 'undefined')
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    return (<>
        {loading && <Loader />}
        <Helmet title="Manage Email Groups" />

        <div className={`${styles.pageTitleWrap}`}>
            <Typography display="inline" className={`${styles.pageTitle}`}>Manage Email Groups</Typography>
            <nav aria-label="breadcrumb">
                <ol className={`${styles.breadcrumb}`}>
                    <li className={`${styles.breadcrumbItem}`}><span><Home /></span>Email Campaigns</li>
                    <li className={`${styles.breadcrumbItem} ${styles.active}`}>Manage Email Groups</li>
                </ol>
            </nav>
        </div>

        <div className={`${styles.BodyCon}`}>
            <div className={`${styles.mainCardDiv}`}>
                <div className={`${styles.mainCard}`}>
                    {contentLoading && <div className="Loader TableLoader"><TableLoaderSmall /></div>}

                    {!contentLoading && <div className="control-pane">
                        <div className={`${styles.Row}`}>

                            <GroupFilter filterData={filterData} setFilterData={setFilterData.bind(this)} setAddModalOpen={setAddModalOpen.bind(this)} />

                            {allData.length > 0 && <>
                                <div className={refetching ? `${styles.LogMaiSec} ${styles.Refetching}` : `${styles.LogMaiSec}`}>

                                    {allData.map((item, index) => {
                                        return (<GroupCard key={item?.id ?? index} item={item} setDeleteModalOpen={setDeleteModalOpen.bind(this)} setSelectecId={setSelectecId.bind(this)} setEditUserData={setEditUserData.bind(this)} setEditModalOpen={setEditModalOpen.bind(this)} />);
                                    })}

                                    {/* Sentinel: entering the viewport loads the next chunk */}
                                    <div ref={sentinelRef} className={styles.ScrollSentinel} />
                                </div>

                                {loadingMore && (
                                    <div className={styles.gridScrollStatus}>
                                        <div className={styles.scrollLoaderWrap}>
                                            <div className={styles.scrollSpinner} />
                                            <span>Loading more groups...</span>
                                        </div>
                                    </div>
                                )}

                                {loadMoreError && (
                                    <div className={styles.gridScrollStatus}>
                                        <p>Couldn't load more groups.</p>
                                        <button type="button" className={styles.RetryBu} onClick={() => fetchGroups(offset, false)}>Retry</button>
                                    </div>
                                )}
                            </>}

                            {allData.length === 0 && loadError && (<div className={`${styles.NoDataMain}`}>
                                <p className={`${styles.NoDataText}`}>Something went wrong</p>
                                <p className={`${styles.NoDataTextSub}`}>We couldn't load groups. Please try again.</p>
                                <button type="button" className={`${styles.RetryBu}`} onClick={() => fetchGroups(0, true)}>Retry</button>
                            </div>)}

                            {allData.length === 0 && !loadError && (<div className={`${styles.NoDataMain}`}>
                                <div className={`${styles.NoDataIMG}`}>
                                    <img src="/static/img/no-prospect.png" alt="No Data" />
                                </div>
                                <p className={`${styles.NoDataText}`}>Let's get started!</p>
                                <p className={`${styles.NoDataTextSub}`}>You don't have any groups saved.</p>
                            </div>)}

                        </div>
                    </div>}
                </div>
            </div>
        </div>

        <Dialog
            open={addModalOpen}
            onClose={(e) => { setAddModalOpen(false) }}
            fullWidth
            maxWidth="sm"
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="NewPopUp MobileForm"
        >
            <DialogTitle id="alert-dialog-title">
                <button className={`${styles.BackAddBU}`} onClick={(e) => { setAddModalOpen(false) }}><ArrowLeft /></button>
                Add Group
                <button onClick={(e) => { setAddModalOpen(false) }}><X /></button>
            </DialogTitle>
            <DialogContent>
                <AddGroup setAddModalOpen={setAddModalOpen.bind(this)} setLoading={setLoading.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMsg={setSnackbarMsg.bind(this)} setAllData={reloadList} editData={null} />
            </DialogContent>
        </Dialog>

        <Dialog
            open={editModalOpen}
            onClose={(e) => { setEditModalOpen(false) }}
            fullWidth
            maxWidth="sm"
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="NewPopUp MobileForm"
        >
            <DialogTitle id="alert-dialog-title">
                <button className={`${styles.BackAddBU}`} onClick={(e) => { setEditModalOpen(false) }}><ArrowLeft /></button>
                Update Group
                <button onClick={(e) => { setEditModalOpen(false) }}><X /></button>
            </DialogTitle>
            <DialogContent>
                <AddGroup setAddModalOpen={setEditModalOpen.bind(this)} setLoading={setLoading.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMsg={setSnackbarMsg.bind(this)} setAllData={setAllData.bind(this)} editData={editUserData} />
            </DialogContent>
        </Dialog>

        <Dialog
            open={deleteModalOpen}
            onClose={(e) => setDeleteModalOpen(false)}
            fullWidth
            maxWidth="sm"
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="NewPopUp"
        >
            <DialogTitle id="alert-dialog-title">Would you like to delete this group?<button onClick={(e) => setDeleteModalOpen(false)}><X /></button></DialogTitle>
            <DialogContent>
                <div className="ModalFormGroup">
                    <label className="PopupBodyText">The group information will be removed immediately.You can't undo this action.</label>
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={(e) => confirmDelete()} className="CancelPopupBU">Delete</Button>
                <Button onClick={(e) => setDeleteModalOpen(false)} className="SubmitPopupBU">Cancel</Button>
            </DialogActions>
        </Dialog>

        <Snackbar
            open={snackbarOpen}
            onClose={(e) => setSnackbarOpen(false)}
            TransitionComponent={Slide}
            message={snackbarMsg}
            autoHideDuration={10000}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            action={
                <IconButton
                    size="small"
                    aria-label="close"
                    color="inherit"
                    onClick={(e) => setSnackbarOpen(false)}
                >
                    <X />
                </IconButton>
            }
        />

        <Footer />
    </>);
}

export default withCustomAuthenticationRequired(Groups, {
    onRedirecting: () => <Loader />,
});